import type { BrowserSettings, BrowserTab, TabGroup } from '../types';
import { faviconFor, toDisplayUrl } from './urls';
import { uid } from './ids';

export function createTab(url: string, settings: BrowserSettings, partial: Partial<BrowserTab> = {}): BrowserTab {
  const actualUrl = url || settings.homepage;
  return {
    id: uid('tab'),
    title: actualUrl.startsWith('websurf://') ? actualUrl.replace('websurf://', 'WebSurf ') : 'New Tab',
    url: actualUrl,
    displayUrl: toDisplayUrl(actualUrl),
    favicon: faviconFor(actualUrl),
    status: 'idle',
    pinned: false,
    zoom: 1,
    canGoBack: false,
    canGoForward: false,
    history: [actualUrl],
    historyIndex: 0,
    viewMode: actualUrl === 'websurf://sandbox' ? 'sandbox' : 'browser',
    reloadKey: 0,
    ...partial
  };
}

export function navigateTab(tab: BrowserTab, url: string): BrowserTab {
  const history = tab.history.slice(0, tab.historyIndex + 1);
  history.push(url);
  return {
    ...tab,
    url,
    displayUrl: toDisplayUrl(url),
    favicon: faviconFor(url) || tab.favicon,
    title: url.startsWith('websurf://') ? url.replace('websurf://', 'WebSurf ') : tab.title,
    status: url.startsWith('websurf://') ? 'complete' : 'loading',
    error: undefined,
    offlineSnapshotId: undefined,
    history,
    historyIndex: history.length - 1,
    canGoBack: history.length > 1,
    canGoForward: false,
    viewMode: url === 'websurf://sandbox' ? 'sandbox' : 'browser'
  };
}

export function goBack(tab: BrowserTab): BrowserTab {
  if (tab.historyIndex <= 0) return tab;
  const historyIndex = tab.historyIndex - 1;
  const url = tab.history[historyIndex];
  return { ...tab, url, displayUrl: toDisplayUrl(url), historyIndex, canGoBack: historyIndex > 0, canGoForward: true, status: url.startsWith('websurf://') ? 'complete' : 'loading', error: undefined };
}

export function goForward(tab: BrowserTab): BrowserTab {
  if (tab.historyIndex >= tab.history.length - 1) return tab;
  const historyIndex = tab.historyIndex + 1;
  const url = tab.history[historyIndex];
  return { ...tab, url, displayUrl: toDisplayUrl(url), historyIndex, canGoBack: true, canGoForward: historyIndex < tab.history.length - 1, status: url.startsWith('websurf://') ? 'complete' : 'loading', error: undefined };
}

export function duplicateTab(tab: BrowserTab): BrowserTab {
  return { ...tab, id: uid('tab'), title: `${tab.title}`, status: 'idle' };
}

export function createGroup(name: string, color: string): TabGroup {
  return { id: uid('grp'), name, color };
}
